import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

interface PanelsContextValue {
  panels: string[];
  addPanel: (atIndex: number) => string;
  closePanel: (panelId: string) => void;
  canClose: boolean;
}

const PanelsContext = createContext<PanelsContextValue | null>(null);

export const PanelsProvider: FC<PropsWithChildren> = ({ children }) => {
  const panelIdCounterRef = useRef(0);
  const generatePanelId = useCallback(() => `panel-${++panelIdCounterRef.current}`, []);

  const [panels, setPanels] = useState<string[]>(() => [generatePanelId()]);

  const addPanel = useCallback(
    (atIndex: number) => {
      const panelId = generatePanelId();
      setPanels((prev) => {
        const newPanels = [...prev];
        newPanels.splice(atIndex, 0, panelId);
        return newPanels;
      });
      return panelId;
    },
    [generatePanelId]
  );

  const closePanel = useCallback((panelId: string) => {
    setPanels((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter((id) => id !== panelId);
    });
  }, []);

  const value = useMemo(
    () => ({
      panels,
      addPanel,
      closePanel,
      canClose: panels.length > 1,
    }),
    [panels, addPanel, closePanel]
  );

  return <PanelsContext.Provider value={value}>{children}</PanelsContext.Provider>;
};

export const usePanels = (): PanelsContextValue => {
  const context = useContext(PanelsContext);
  if (!context) {
    throw new Error("usePanels must be used within a PanelsProvider");
  }
  return context;
};
